import { PIN_RE } from '@mm/shared';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Lock, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Card, EmptyState } from '../../components/ui/display';
import { Field, Input } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { formatDate } from '../../utils/format';
import { SECURITY_KEY, useAuth } from './AuthProvider';

const message = (err: unknown) =>
  err instanceof ApiError
    ? [err.message, ...err.errors.map((e) => e.message)]
        .filter((m, i, a) => m && a.indexOf(m) === i)
        .join(' ')
    : err instanceof Error && err.name === 'NotAllowedError'
      ? 'Cancelled.'
      : err instanceof Error && err.name === 'InvalidStateError'
        ? 'This device is already set up.'
        : 'Something went wrong';

/** Set or change the PIN. The first PIN needs no password; changing it does. */
export function PinForm({ hasPin, onDone }: { hasPin: boolean; onDone?: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post('/auth/pin', { pin, currentPassword: hasPin ? password : undefined }),
    onSuccess: () => {
      toast.success(hasPin ? 'PIN changed' : 'PIN set');
      setPin('');
      setConfirm('');
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: SECURITY_KEY });
      onDone?.();
    },
    onError: (err) => setError(message(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!PIN_RE.test(pin)) return setError('The PIN must be 4 to 6 digits');
    if (pin !== confirm) return setError('The two PINs do not match');
    save.mutate();
  };
  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 6);
  return (
    <form onSubmit={submit} className="max-w-sm space-y-4">
      {error && <Alert tone="error">{error}</Alert>}
      <Field label={hasPin ? 'New PIN' : 'PIN'} htmlFor="pin" help="4 to 6 digits">
        <Input
          id="pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(digits(e.target.value))}
        />
      </Field>
      <Field label="Repeat the PIN" htmlFor="pin-confirm">
        <Input
          id="pin-confirm"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={confirm}
          onChange={(e) => setConfirm(digits(e.target.value))}
        />
      </Field>
      {hasPin && (
        <Field label="Current password" htmlFor="pin-password">
          <Input
            id="pin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      )}
      <Button type="submit" loading={save.isPending}>
        {hasPin ? 'Change PIN' : 'Set PIN'}
      </Button>
    </form>
  );
}

/** Remove the PIN (needs the password). */
function TurnOffPin() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const off = useMutation({
    mutationFn: () => api.delete('/auth/pin', { currentPassword: password }),
    onSuccess: () => {
      toast.success('PIN turned off');
      setOpen(false);
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: SECURITY_KEY });
    },
    onError: (err) => setError(message(err)),
  });
  return (
    <div className="mt-6 border-t border-slate-100 pt-4">
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Turn off PIN
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Turn off the PIN?"
        description="Without a PIN or fingerprint / Face ID, the app no longer locks after inactivity."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={off.isPending}
              disabled={!password}
              onClick={() => {
                setError(null);
                off.mutate();
              }}
            >
              Turn off
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        )}
        <Field label="Current password" htmlFor="pin-off-password">
          <Input
            id="pin-off-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}

const guessDeviceName = () => {
  const ua = navigator.userAgent;
  const os = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad/.test(ua)
      ? 'iPhone / iPad'
      : /Mac/.test(ua)
        ? 'Mac'
        : /Windows/.test(ua)
          ? 'Windows PC'
          : 'This device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : '';
  return browser ? `${os} · ${browser}` : os;
};

/** PIN and fingerprint / Face ID devices of the signed-in user. */
export function SecuritySettings() {
  const { security } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [removing, setRemoving] = useState<{ id: string; deviceName: string } | null>(null);
  const supported = browserSupportsWebAuthn();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: SECURITY_KEY });

  const add = useMutation({
    mutationFn: async () => {
      const options =
        await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>(
          '/auth/passkeys/options',
        );
      const response = await startRegistration({ optionsJSON: options });
      await api.post('/auth/passkeys', { response, deviceName });
    },
    onSuccess: () => {
      toast.success('Fingerprint / Face ID is set up on this device');
      setNaming(false);
      refresh();
    },
    onError: (err) => toast.error(message(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/passkeys/${id}`),
    onSuccess: () => {
      toast.success('Device removed');
      setRemoving(null);
      refresh();
    },
    onError: (err) => toast.error(message(err)),
  });

  if (!security) return null;
  const lockOn = security.hasPin || security.passkeys.length > 0;
  return (
    <>
      <Card
        title={
          <span className="flex items-center gap-2">
            <Lock className="size-4 text-slate-400" /> App lock PIN
          </span>
        }
        description={
          lockOn
            ? `App lock is on: the app locks after ${security.idleLockMinutes} minutes without activity. Unlock with your PIN, fingerprint / Face ID, or your password.`
            : `Optional. Set a PIN (or fingerprint / Face ID below) to lock the app after ${security.idleLockMinutes} minutes without activity.`
        }
        actions={
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${lockOn ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
          >
            App lock {lockOn ? 'on' : 'off'}
          </span>
        }
      >
        <PinForm hasPin={security.hasPin} />
        {security.hasPin && <TurnOffPin />}
      </Card>
      <Card
        title={
          <span className="flex items-center gap-2">
            <Fingerprint className="size-4 text-slate-400" /> Fingerprint / Face ID
          </span>
        }
        description="Unlock, and sign in without the password, on devices you set up here."
        actions={
          supported && (
            <Button
              size="sm"
              onClick={() => {
                setDeviceName(guessDeviceName());
                setNaming(true);
              }}
            >
              Set up on this device
            </Button>
          )
        }
        bodyClassName="p-0"
      >
        {!supported && (
          <p className="px-5 pt-4 text-sm text-amber-700">
            This browser does not support fingerprint / Face ID sign-in. Open the app over HTTPS in
            a current browser.
          </p>
        )}
        {security.passkeys.length === 0 ? (
          <EmptyState title="No devices set up yet" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {security.passkeys.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <span>
                  <span className="font-medium text-slate-900">{p.deviceName}</span>
                  <span className="block text-xs text-slate-500">
                    Added {formatDate(p.createdAt.slice(0, 10))}
                    {p.lastUsedAt ? ` · last used ${formatDate(p.lastUsedAt.slice(0, 10))}` : ''}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${p.deviceName}`}
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setRemoving(p)}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={naming}
        onClose={() => setNaming(false)}
        title="Set up fingerprint / Face ID"
        description="Your phone or computer will ask for your fingerprint, face or screen lock."
        footer={
          <>
            <Button variant="secondary" onClick={() => setNaming(false)}>
              Cancel
            </Button>
            <Button
              loading={add.isPending}
              disabled={!deviceName.trim()}
              onClick={() => add.mutate()}
            >
              Continue
            </Button>
          </>
        }
      >
        <Field label="Device name" htmlFor="device-name">
          <Input
            id="device-name"
            value={deviceName}
            maxLength={100}
            onChange={(e) => setDeviceName(e.target.value)}
          />
        </Field>
      </Modal>
      <ConfirmDialog
        open={!!removing}
        title="Remove this device?"
        message={removing && `${removing.deviceName} will no longer unlock or sign in.`}
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
